import { Order } from '@/data/mockData';
import { OutputFormat } from '@/constants/outputFormat';

/**
 * Interface pour une demande retournée par l'API GET /requests/client/{client_id}
 */
export interface ApiRequest {
  id: number;
  name: string;
  user_email: string;
  client_id: number;
  output_format?: OutputFormat;
  selected_segments: {
    [categoryName: string]: Array<{
      name: string;
      name_data: string;
      type: string;
    }>;
  };
  days_back: number;
  requested_volume: number;
  max_volume: number;
  id_type: string;
  final_price_eur: string;
  date: string; // ISO format
  status: 'NEW_REQUEST' | 'QUOTE_REQUESTED' | 'RUNNING' | 'SUCCESS' | 'ERROR';
  bucket_output_url?: string;
  crm_file_label: string;
  crm_exclusion?: string[];
  // Choix par population (cf. contract.md §3) ; absent/null si pas de population.
  population_requests?: Array<{
    population: string;
    id_type: string;
    requested_volume: number;
  }>;
}

/**
 * Mapper id_type de l'API vers le type Order
 */
const mapIdTypeToOrderType = (idType: string): 'Email' | 'Mobile' | 'CRM ID' => {
  if (idType === 'emails') {
    return 'Email';
  }
  if (idType === 'phones') {
    return 'Mobile';
  }
  // "emails OR phones" ou "emails AND phones" → "CRM ID"
  return 'CRM ID';
};

/**
 * Transformer selected_segments de l'API vers le format Order
 */
const mapSelectedSegments = (
  apiSegments: ApiRequest['selected_segments']
): Order['selectedSegments'] => {
  if (!apiSegments) {
    return undefined;
  }

  const result: Order['selectedSegments'] = [];

  Object.entries(apiSegments).forEach(([categoryName, segments]) => {
    segments.forEach((segment) => {
      // Utiliser name_data comme segmentId, ou name si name_data n'est pas disponible
      const segmentId = segment.name_data || segment.name;
      result.push({
        categoryName,
        segmentName: segment.name,
        segmentId,
      });
    });
  });

  return result.length > 0 ? result : undefined;
};

/**
 * Convertir days_back en duration
 */
const mapDaysBackToDuration = (daysBack: number): '30' | '60' | '90' | undefined => {
  if (daysBack === 30) return '30';
  if (daysBack === 60) return '60';
  if (daysBack === 90) return '90';
  return undefined;
};

/**
 * Extraire la date au format YYYY-MM-DD depuis une chaîne ISO
 */
const extractDate = (isoDate: string): string => {
  try {
    const date = new Date(isoDate);
    return date.toISOString().split('T')[0];
  } catch {
    return isoDate.split('T')[0]; // Fallback simple
  }
};

/**
 * Construire l'URL GCS complète depuis bucket_output_url
 */
const buildGcsUrl = (bucketOutputUrl?: string): string | undefined => {
  if (!bucketOutputUrl) {
    return undefined;
  }
  // Préfixer avec https://console.cloud.google.com/storage/browser/_details/
  return `https://console.cloud.google.com/storage/browser/_details/${bucketOutputUrl}`;
};

/**
 * Mapper une demande API vers un Order
 */
export const mapApiRequestToOrder = (apiRequest: ApiRequest): Order => {
  const order: Order = {
    id: apiRequest.id.toString(),
    requestName: apiRequest.name,
    volume: apiRequest.requested_volume,
    price: parseFloat(apiRequest.final_price_eur) || 0,
    date: extractDate(apiRequest.date),
    owner: apiRequest.user_email,
    status: apiRequest.status,
    type: mapIdTypeToOrderType(apiRequest.id_type),
    idType: apiRequest.id_type,
    duration: mapDaysBackToDuration(apiRequest.days_back),
    selectedCrm: apiRequest.crm_file_label,
    selectedSegments: mapSelectedSegments(apiRequest.selected_segments),
    crmExclusion: apiRequest.crm_exclusion || [],
    outputFormat: apiRequest.output_format,
  };

  // Détail par population (cf. contract.md §3) : conservé pour l'affichage dans
  // l'historique (détail de la commande + types multiples dans la colonne Type).
  if (apiRequest.population_requests && apiRequest.population_requests.length > 0) {
    order.populationRequests = apiRequest.population_requests.map((pr) => ({
      population: pr.population,
      type: mapIdTypeToOrderType(pr.id_type),
      idType: pr.id_type,
      volume: pr.requested_volume,
    }));
  }

  // Ajouter resultUrl seulement si status = SUCCESS et bucket_output_url est présent
  if (apiRequest.status === 'SUCCESS' && apiRequest.bucket_output_url) {
    order.resultUrl = buildGcsUrl(apiRequest.bucket_output_url);
  }

  return order;
};

/**
 * Mapper un tableau de demandes API vers un tableau d'Orders
 */
export const mapApiRequestsToOrders = (apiRequests: ApiRequest[]): Order[] => {
  return apiRequests.map(mapApiRequestToOrder);
};

